import React, { useEffect, useState, useRef } from 'react';
import { SearchIcon, X, UserIcon, MapPinIcon, ChevronDownIcon } from 'lucide-react';
import { useUserProfile, cityPresets } from '../context/UserProfileContext';
import { TravelPartySelector } from './TravelPartySelector';
interface SearchBarProps {
  isMobile?: boolean;
}
export const SearchBar: React.FC<SearchBarProps> = ({
  isMobile = false
}) => {
  const {
    searchQuery,
    setSearchQuery,
    recentSearches,
    addRecentSearch,
    travelParty,
    applyPresetPOIs
  } = useUserProfile();
  const [isActive, setIsActive] = useState(false);
  const [showTravelParty, setShowTravelParty] = useState(false);
  const [citySuggestions, setCitySuggestions] = useState<string[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);
  // Generate city suggestions based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setCitySuggestions([]);
      return;
    }
    const query = searchQuery.toLowerCase();
    const matches = Object.keys(cityPresets).filter(city => city.includes(query) || cityPresets[city].name.toLowerCase().includes(query));
    setCitySuggestions(matches);
  }, [searchQuery]);
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      addRecentSearch(searchQuery);
      // Check if the search query matches a city
      const cityKey = Object.keys(cityPresets).find(city => city === searchQuery.toLowerCase() || cityPresets[city].name.toLowerCase() === searchQuery.toLowerCase());
      if (cityKey) {
        applyPresetPOIs(cityKey);
      }
    }
    setIsActive(false);
  };
  const handleCitySelect = (cityKey: string) => {
    setSearchQuery(cityPresets[cityKey].name);
    addRecentSearch(cityPresets[cityKey].name);
    applyPresetPOIs(cityKey);
    setIsActive(false);
  };
  const handleRecentSearchClick = (search: string) => {
    setSearchQuery(search);
    addRecentSearch(search);
    // Check if the search is a city
    const cityKey = Object.keys(cityPresets).find(city => city === search.toLowerCase() || cityPresets[city].name.toLowerCase() === search.toLowerCase());
    if (cityKey) {
      applyPresetPOIs(cityKey);
    }
    setIsActive(false);
  };
  const clearSearch = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSearchQuery('');
  };
  const handleTravelPartyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowTravelParty(!showTravelParty);
    setIsActive(false);
  };
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsActive(false);
        setShowTravelParty(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Map travel party type to label
  const partyLabels = {
    couple: 'Couples',
    youngster: 'Youngsters',
    family: 'Families'
  };
  return <div className={`relative ${isMobile ? 'w-full' : ''}`} ref={searchRef} data-id="element-196">
      <div className={`flex items-center border rounded-full shadow-sm transition ${isActive ? 'shadow-md' : 'hover:shadow-md'} ${isMobile ? 'w-full' : ''} bg-white`} data-id="element-197">
        <button type="button" className="px-4 py-3 cursor-pointer flex items-center gap-2 hover:bg-gray-50 rounded-l-full transition border-none bg-transparent outline-none" onClick={handleTravelPartyClick} data-id="element-198">
          <UserIcon size={18} className="text-rose-500" data-id="element-199" />
          <span className="text-sm font-medium whitespace-nowrap" data-id="element-200">
            {partyLabels[travelParty]}
          </span>
          <ChevronDownIcon size={16} className={`text-gray-400 transition-transform ${showTravelParty ? 'rotate-180' : ''}`} data-id="element-201" />
        </button>
        <div className="h-6 border-r border-gray-300" data-id="element-202"></div>
        <form onSubmit={handleSearch} className="flex-1 flex items-center" data-id="element-203">
          <input type="text" placeholder="Where are you going?" className="px-4 py-2 w-full outline-none rounded-full text-sm" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onFocus={() => {
          setIsActive(true);
          setShowTravelParty(false);
        }} autoFocus={isMobile} data-id="element-204" />
          {searchQuery && <button type="button" onClick={clearSearch} className="text-gray-400 hover:text-gray-600 mr-1 p-1" data-id="element-205">
              <X size={16} data-id="element-206" />
            </button>}
        </form>
        <button type="button" onClick={handleSearch} className="bg-rose-500 p-2 rounded-full text-white mr-1 hover:bg-rose-600 transition" data-id="element-207">
          <SearchIcon size={16} data-id="element-208" />
        </button>
      </div>
      {/* Travel Party Selector Dropdown */}
      {showTravelParty && <div className={`${isMobile ? 'absolute top-full mt-2 left-0 right-0' : 'absolute top-full mt-2 left-0'} z-[1000]`} onClick={e => e.stopPropagation()} data-id="element-209">
          <TravelPartySelector onClose={() => setShowTravelParty(false)} data-id="element-210" />
        </div>}
      {/* City Suggestions Dropdown */}
      {isActive && searchQuery && citySuggestions.length > 0 && !showTravelParty && <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-lg shadow-lg p-3 z-[999]" onClick={e => e.stopPropagation()} data-id="element-211">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-212">
              Suggested Cities
            </h3>
            <div className="space-y-2" data-id="element-213">
              {citySuggestions.map(cityKey => <div key={cityKey} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-md cursor-pointer" onClick={() => handleCitySelect(cityKey)} data-id="element-214">
                  <MapPinIcon size={14} className="text-rose-500" data-id="element-215" />
                  <span data-id="element-216">{cityPresets[cityKey].name}</span>
                </div>)}
            </div>
          </div>}
      {/* Recent Searches Dropdown */}
      {isActive && recentSearches.length > 0 && !searchQuery && !showTravelParty && <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-lg shadow-lg p-3 z-[999]" onClick={e => e.stopPropagation()} data-id="element-217">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-218">
              Recent Searches
            </h3>
            <div className="space-y-2" data-id="element-219">
              {recentSearches.map((search, index) => <div key={index} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-md cursor-pointer" onClick={() => handleRecentSearchClick(search)} data-id="element-220">
                  <SearchIcon size={14} className="text-gray-400" data-id="element-221" />
                  <span data-id="element-222">{search}</span>
                </div>)}
            </div>
          </div>}
    </div>;
};